import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft,
  Copy,
  Loader2,
  Sticker,
  Cpu,
  TestTube2,
  ExternalLink,
  UserPlus,
  UserMinus,
  ArrowLeftRight,
  Ban,
  Power,
  CheckCircle2,
  Circle,
  PackageCheck,
  Building2,
} from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Separator } from "@/components/ui/separator";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const STATUS_LABELS = {
  free: { label: "Frei", cls: "border-white/10 text-muted-foreground bg-white/[0.03]" },
  assigned: { label: "Zugewiesen", cls: "border-sky-500/30 text-sky-400 bg-sky-500/10" },
  preparing: { label: "In Vorbereitung", cls: "border-amber-500/30 text-amber-400 bg-amber-500/10" },
  ready: { label: "Verkaufsbereit", cls: "border-primary/30 text-primary bg-primary/10" },
  delivered: { label: "Ausgeliefert", cls: "border-emerald-500/30 text-emerald-400 bg-emerald-500/10" },
  disabled: { label: "Deaktiviert", cls: "border-destructive/40 text-destructive bg-destructive/10" },
};

export default function StandDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [stand, setStand] = useState(null);
  const [loading, setLoading] = useState(true);
  const [customers, setCustomers] = useState([]);
  const [assignOpen, setAssignOpen] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/stands/${id}`);
      setStand(data);
    } catch (e) {
      toast.error(formatApiError(e));
      navigate("/aufsteller");
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    api
      .get("/customers")
      .then(({ data }) => setCustomers(data.customers || []))
      .catch(() => {});
  }, []);

  const patchChecklist = async (key, value) => {
    try {
      const { data } = await api.patch(`/stands/${id}/checklist`, { key, value });
      setStand(data);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(stand.nfc_link);
      toast.success("Stand-URL kopiert.");
      if (!stand.checklist?.nfc_copied && stand.customer_id) {
        patchChecklist("nfc_copied", true);
      }
    } catch {
      toast.error("Kopieren fehlgeschlagen.");
    }
  };

  const testQr = () => {
    window.open(stand.nfc_link, "_blank", "noopener");
    if (stand.customer_id && !stand.checklist?.qr_tested) {
      patchChecklist("qr_tested", true);
    }
  };

  const testNfc = () => {
    window.open(stand.nfc_link, "_blank", "noopener");
    if (stand.customer_id && !stand.checklist?.nfc_tested) {
      patchChecklist("nfc_tested", true);
    }
  };

  const unassign = async () => {
    try {
      const { data } = await api.post(`/stands/${id}/unassign`);
      setStand(data);
      toast.success("Aufsteller freigegeben.");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const toggleDisabled = async () => {
    try {
      const { data } = await api.patch(`/stands/${id}`, {
        disabled: !stand.disabled,
      });
      setStand(data);
      toast.success(data.disabled ? "Aufsteller deaktiviert." : "Aufsteller aktiviert.");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  if (loading || !stand) {
    return (
      <div className="text-muted-foreground text-sm flex items-center gap-2">
        <Loader2 className="w-4 h-4 animate-spin" /> Lädt…
      </div>
    );
  }

  const cl = stand.checklist || {};
  const status = STATUS_LABELS[stand.status] || STATUS_LABELS.free;
  const isAssigned = !!stand.customer_id;

  return (
    <div className="space-y-6" data-testid="stand-detail-page">
      <div className="flex items-center justify-between gap-3">
        <Link
          to="/aufsteller"
          className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1.5"
          data-testid="back-to-stands"
        >
          <ArrowLeft className="w-3 h-3" /> Zurück
        </Link>
        <Badge
          variant="outline"
          className={status.cls}
          data-testid="detail-stand-status"
        >
          {status.label}
        </Badge>
      </div>

      <div>
        <p className="label-eyebrow">Aufsteller</p>
        <h1
          className="font-heading text-3xl sm:text-4xl font-bold tracking-tight mt-1 font-mono"
          data-testid="detail-serial"
        >
          {stand.serial_number}
        </h1>
        <p className="text-sm text-muted-foreground mt-1">
          Physischer Bewertungs-Aufsteller. Serial &amp; Stand-Code sind unveränderlich.
        </p>
      </div>

      <div className="grid lg:grid-cols-2 gap-6 items-start">
        <div className="card-surface p-5" data-testid="detail-info-card">
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div>
              <div className="label-eyebrow">Stand-Code</div>
              <div
                className="font-mono text-primary text-lg mt-1"
                data-testid="detail-stand-code"
              >
                {stand.stand_code}
              </div>
            </div>
            <div>
              <div className="label-eyebrow">Serial-Nummer</div>
              <div className="font-mono text-lg mt-1">{stand.serial_number}</div>
            </div>
          </div>
          <Separator className="my-4 bg-white/[0.06]" />
          <div className="label-eyebrow">Kunde</div>
          {stand.customer ? (
            <Link
              to={`/kunden/${stand.customer.id}`}
              className="mt-1 flex items-center gap-2 text-primary hover:underline"
              data-testid="detail-customer-link"
            >
              <Building2 className="w-4 h-4" />
              {stand.customer.business_name}
            </Link>
          ) : (
            <p className="text-sm text-muted-foreground mt-1">— frei / nicht zugewiesen —</p>
          )}

          <Separator className="my-4 bg-white/[0.06]" />
          <div className="label-eyebrow mb-1">Aufsteller-URL (QR &amp; NFC identisch)</div>
          <div
            className="font-mono text-xs bg-white/[0.02] px-3 py-2 rounded border border-white/[0.05] break-all"
            data-testid="detail-nfc-link"
          >
            {stand.nfc_link}
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-4">
            <Link to={`/aufsteller/${stand.id}/label`} data-testid="detail-label-btn">
              <Button size="sm" className="w-full rounded-full gap-1.5">
                <Sticker className="w-3.5 h-3.5" /> Label
              </Button>
            </Link>
            <Button
              size="sm"
              variant="secondary"
              onClick={copyLink}
              className="w-full rounded-full gap-1.5"
              data-testid="detail-copy-btn"
            >
              <Copy className="w-3.5 h-3.5" /> Link kopieren
            </Button>
            <Button
              size="sm"
              variant="secondary"
              onClick={testQr}
              className="w-full rounded-full gap-1.5"
              data-testid="detail-test-qr-btn"
            >
              <TestTube2 className="w-3.5 h-3.5" /> QR testen
            </Button>
            <Button
              size="sm"
              variant="secondary"
              onClick={testNfc}
              className="w-full rounded-full gap-1.5"
              data-testid="detail-test-nfc-btn"
            >
              <TestTube2 className="w-3.5 h-3.5" /> NFC testen
            </Button>
          </div>

          <div className="flex flex-wrap gap-2 mt-3">
            {isAssigned ? (
              <>
                {stand.customer && (
                  <Link to={`/kunden/${stand.customer.id}/nfc`} data-testid="detail-nfc-wizard-btn">
                    <Button size="sm" variant="ghost" className="rounded-full gap-1.5">
                      <Cpu className="w-3.5 h-3.5" /> NFC-Assistent
                    </Button>
                  </Link>
                )}
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => setAssignOpen(true)}
                  className="rounded-full gap-1.5"
                  data-testid="detail-reassign-btn"
                >
                  <ArrowLeftRight className="w-3.5 h-3.5" /> Neu zuweisen
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={unassign}
                  className="rounded-full gap-1.5"
                  data-testid="detail-unassign-btn"
                >
                  <UserMinus className="w-3.5 h-3.5" /> Freigeben
                </Button>
              </>
            ) : (
              <Button
                size="sm"
                onClick={() => setAssignOpen(true)}
                className="rounded-full gap-1.5"
                data-testid="detail-assign-btn"
              >
                <UserPlus className="w-3.5 h-3.5" /> Zuweisen
              </Button>
            )}
            <Button
              size="sm"
              variant="ghost"
              onClick={toggleDisabled}
              className="rounded-full gap-1.5"
              data-testid="detail-toggle-disabled-btn"
            >
              {stand.disabled ? (
                <>
                  <Power className="w-3.5 h-3.5" /> Aktivieren
                </>
              ) : (
                <>
                  <Ban className="w-3.5 h-3.5" /> Deaktivieren
                </>
              )}
            </Button>
          </div>
        </div>

        <div className="card-surface p-5" data-testid="detail-checklist-card">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-heading text-lg font-semibold">Onboarding-Checkliste</h3>
            <Link
              to={
                stand.customer_id
                  ? `/kunden/${stand.customer_id}`
                  : "#"
              }
              className={`text-xs ${
                stand.customer_id ? "text-primary hover:underline" : "pointer-events-none text-muted-foreground"
              }`}
            >
              Zum Kunden &rarr;
            </Link>
          </div>
          {!isAssigned && (
            <div className="text-xs text-muted-foreground mb-3">
              Erst nach Zuweisung an einen Kunden verfügbar.
            </div>
          )}
          <ul className="space-y-2" data-testid="detail-checklist">
            <ChecklistItem label="Aufsteller angelegt" done autoLocked />
            <ChecklistItem
              label="QR-Code generiert"
              done
              autoLocked
              testId="detail-cl-qr_generated"
            />
            <ChecklistItem
              label="QR gedruckt"
              done={!!cl.qr_downloaded}
              disabled={!isAssigned}
              onChange={(v) => patchChecklist("qr_downloaded", v)}
              testId="detail-cl-qr_downloaded"
            />
            <ChecklistItem
              label="NFC programmiert"
              done={!!cl.nfc_copied}
              disabled={!isAssigned}
              onChange={(v) => patchChecklist("nfc_copied", v)}
              testId="detail-cl-nfc_copied"
            />
            <ChecklistItem
              label="QR getestet"
              done={!!cl.qr_tested}
              disabled={!isAssigned}
              onChange={(v) => patchChecklist("qr_tested", v)}
              testId="detail-cl-qr_tested"
            />
            <ChecklistItem
              label="NFC getestet"
              done={!!cl.nfc_tested}
              disabled={!isAssigned}
              onChange={(v) => patchChecklist("nfc_tested", v)}
              testId="detail-cl-nfc_tested"
            />
            <ChecklistItem
              label="An Kunden ausgeliefert"
              done={!!cl.delivered}
              disabled={!isAssigned}
              onChange={(v) => patchChecklist("delivered", v)}
              icon={PackageCheck}
              testId="detail-cl-delivered"
            />
          </ul>
        </div>
      </div>

      <StandAssignDialog
        open={assignOpen}
        stand={stand}
        customers={customers}
        onClose={() => setAssignOpen(false)}
        onDone={(next) => {
          setAssignOpen(false);
          setStand(next);
        }}
      />
    </div>
  );
}

function ChecklistItem({ label, done, onChange, autoLocked, disabled, testId, icon: Icon }) {
  return (
    <li className="flex items-center gap-3">
      {autoLocked ? (
        <div
          className="h-4 w-4 rounded border border-emerald-500/40 bg-emerald-500/20 grid place-items-center shrink-0"
          data-testid={testId}
        >
          <CheckCircle2 className="w-3 h-3 text-emerald-400" />
        </div>
      ) : (
        <Checkbox
          checked={done}
          disabled={disabled}
          onCheckedChange={(v) => onChange?.(!!v)}
          data-testid={testId}
        />
      )}
      <span className={`text-sm flex-1 ${done ? "line-through text-muted-foreground" : ""}`}>
        {label}
      </span>
      {Icon ? (
        <Icon className={`w-3.5 h-3.5 ${done ? "text-emerald-400" : "text-muted-foreground/50"}`} />
      ) : done ? (
        <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400" />
      ) : (
        <Circle className="w-3.5 h-3.5 text-muted-foreground/50" />
      )}
    </li>
  );
}

function StandAssignDialog({ open, stand, customers, onClose, onDone }) {
  const [selected, setSelected] = useState("");
  const [busy, setBusy] = useState(false);
  const isReassign = !!stand?.customer_id;

  useEffect(() => {
    setSelected("");
  }, [open]);

  const submit = async () => {
    if (!selected || !stand) return;
    setBusy(true);
    try {
      const path = isReassign ? "reassign" : "assign";
      const { data } = await api.post(`/stands/${stand.id}/${path}`, {
        customer_id: selected,
      });
      toast.success(isReassign ? "Neu zugewiesen." : "Zugewiesen.");
      onDone(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="detail-assign-dialog">
        <DialogHeader>
          <DialogTitle>
            {isReassign ? "Neu zuweisen" : "Aufsteller zuweisen"} — {stand?.serial_number}
          </DialogTitle>
          <DialogDescription>
            Serial-Nummer und Stand-Code bleiben unverändert. Das gedruckte Label muss nicht
            neu erstellt werden.
          </DialogDescription>
        </DialogHeader>
        <div>
          <label className="label-eyebrow">Kunde</label>
          <Select value={selected} onValueChange={setSelected}>
            <SelectTrigger
              className="mt-1 h-11 bg-background/50"
              data-testid="detail-assign-select"
            >
              <SelectValue placeholder="Kunde auswählen…" />
            </SelectTrigger>
            <SelectContent>
              {customers.map((c) => (
                <SelectItem key={c.id} value={c.id}>
                  {c.business_name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            Abbrechen
          </Button>
          <Button
            onClick={submit}
            disabled={!selected || busy}
            className="rounded-full gap-2"
            data-testid="detail-assign-submit-btn"
          >
            {busy && <Loader2 className="w-4 h-4 animate-spin" />}
            {isReassign ? "Neu zuweisen" : "Zuweisen"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
